<script lang="ts">
  // ============================================================
  // ⚠️ Svelte 4 Legacy Mode Component
  // ------------------------------------------------------------
  // @inertiajs/svelte 호환성을 위해 Svelte 4 문법 유지
  // ============================================================

  import { router } from '@inertiajs/svelte'
  import { formatRelativeTime } from '@/lib/utils'
  import CategoryBadge from '@/components/ui/CategoryBadge.svelte'

  interface Pagination {
    current_page: number;
    total_pages: number;
  }

  export let board: any = {}
  export let posts: any[] = []
  export let notices: any[] = []
  export let pagination: Pagination = {} as Pagination
  export let sort: string = 'recent'
  
  function changeSort(newSort: string) {
    router.get(`/boards/${board.slug}/posts`, { sort: newSort }, { preserveState: true })
  }
  
  function goToPage(page: number) {
    router.get(`/boards/${board.slug}/posts`, { page, sort }, { preserveState: true })
  }
</script>

<div class="space-y-6">
  <!-- Header -->
  <header class="bg-surface border-b border-default">
    <div class="py-6">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-caption text-content-tertiary mb-4">
        <a href="/boards" class="hover:text-primary transition-colors cursor-pointer">게시판</a>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
        <span class="text-content">{board.name}</span>
      </nav>

      <div class="flex items-center justify-between">
        <h1 class="text-h1 text-content">{board.name}</h1>
        <a
          href={`/boards/${board.slug}/posts/new`}
          class="btn btn-primary btn-md"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          글쓰기
        </a>
      </div>
    </div>
  </header>
  
  <!-- Sort Tabs -->
  <div class="bg-surface border-b border-default">
    <div>
      <div class="flex items-center gap-1">
        <button
          onclick={() => changeSort('recent')}
          class="px-4 py-3 text-label border-b-2 transition-all cursor-pointer {sort === 'recent'
            ? 'text-primary border-primary'
            : 'text-content-tertiary border-transparent hover:text-gray-900 dark:hover:text-dark-text-high'}"
        >
          최신순
        </button>
        <button
          onclick={() => changeSort('hot')}
          class="px-4 py-3 text-label border-b-2 transition-all cursor-pointer {sort === 'hot'
            ? 'text-primary border-primary'
            : 'text-content-tertiary border-transparent hover:text-gray-900 dark:hover:text-dark-text-high'}"
        >
          인기순
        </button>
        <button
          onclick={() => changeSort('views')}
          class="px-4 py-3 text-label border-b-2 transition-all cursor-pointer {sort === 'views'
            ? 'text-primary border-primary'
            : 'text-content-tertiary border-transparent hover:text-gray-900 dark:hover:text-dark-text-high'}"
        >
          조회순
        </button>
      </div>
    </div>
  </div>
  
  <main class="py-6">
    <!-- Notices -->
    {#if notices.length > 0}
      <div class="mb-8 space-y-3">
        {#each notices as notice}
          <a
            href={`/boards/${board.slug}/posts/${notice.id}`}
            class="flex items-center gap-3 px-5 py-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <span class="badge badge-warning font-semibold"><svg class="w-3.5 h-3.5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>공지</span>
            <span class="text-body2 font-semibold text-content flex-1 truncate">{notice.title}</span>
            <span class="text-caption text-content-tertiary whitespace-nowrap ml-2">{formatRelativeTime(notice.created_at)}</span>
          </a>
        {/each}
      </div>
    {/if}
    
    <!-- Posts -->
    <div class="space-y-3">
      {#each posts as post (post.id)}
        <a
          href={`/boards/${board.slug}/posts/${post.id}`}
          class="block card p-5 border border-default hover:shadow-md dark:hover:shadow-black/40 hover:border-gray-300 dark:hover:border-slate-500 transition-all duration-200"
        >
          <div class="flex items-start gap-4">
            <!-- Content -->
            <div class="flex-1 min-w-0">
              <!-- Badges -->
              <div class="flex items-center gap-2 mb-2">
                {#if post.is_pinned}
                  <span class="badge badge-warning text-xs font-semibold"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg></span>
                {/if}
                {#each post.categories || [] as category}
                  <CategoryBadge {category} />
                {/each}
              </div>

              <!-- Title -->
              <h3 class="text-title font-semibold text-content group-hover:text-primary transition-colors truncate">
                {post.title}
                {#if post.comments_count && post.comments_count > 0}
                  <span class="text-primary text-label ml-1.5 font-bold">[{post.comments_count}]</span>
                {/if}
              </h3>

              <!-- Meta -->
              <div class="flex items-center gap-4 mt-2.5 text-caption text-content-tertiary">
                <span class="font-medium">{post.user?.name || '익명'}</span>
                <span>{formatRelativeTime(post.created_at)}</span>
              </div>
            </div>

            <!-- Stats -->
            <div class="flex items-center gap-5 text-caption">
              <div class="text-right">
                <div class="text-body2 font-semibold text-content">{post.views_count || 0}</div>
                <div class="text-content-tertiary text-xs">조회</div>
              </div>
              <div class="text-right">
                <div class="text-body2 font-semibold text-content">{post.likes_count || 0}</div>
                <div class="text-content-tertiary text-xs">추천</div>
              </div>
            </div>
          </div>
        </a>
      {/each}
      
      {#if posts.length === 0}
        <div class="card p-12 text-center border-2 border-dashed border-default">
          <div class="w-20 h-20 bg-surface-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h3 class="text-h2 text-content mb-2">게시글이 없습니다</h3>
          <p class="text-body2 text-content-tertiary">첫 번째 글을 작성해보세요!</p>
        </div>
      {/if}
    </div>
    
    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-8">
        <button
          onclick={() => goToPage(pagination.current_page - 1)}
          disabled={pagination.current_page === 1}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          이전
        </button>
        
        <div class="flex items-center gap-1">
          {#each Array(Math.min(pagination.total_pages, 10)) as _, i}
            <button
              onclick={() => goToPage(i + 1)}
              class="w-10 h-10 rounded-xl text-label transition-colors {pagination.current_page === i + 1
                ? 'bg-primary text-white'
                : 'bg-surface-tertiary text-gray-700 dark:text-dark-text-medium hover:bg-gray-200 dark:hover:bg-dark-elevated'}"
            >
              {i + 1}
            </button>
          {/each}
        </div>
        
        <button
          onclick={() => goToPage(pagination.current_page + 1)}
          disabled={pagination.current_page === pagination.total_pages}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          다음
          <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    {/if}
  </main>
</div>
